/**
 * Custom Fields Type Definitions (Frontend)
 * Allows dynamic custom fields for customer data
 */

// ==================== Field Types ====================

export type CustomFieldType =
  | 'text'           // Single-line text input
  | 'textarea'       // Multi-line text input
  | 'number'         // Numeric input
  | 'decimal'        // Decimal number input
  | 'currency'       // Currency input
  | 'percentage'     // Percentage input
  | 'email'          // Email input with validation
  | 'phone'          // Phone number input
  | 'url'            // URL input with validation
  | 'date'           // Date picker
  | 'datetime'       // Date and time picker
  | 'select'         // Single selection dropdown
  | 'multiselect'    // Multiple selection dropdown
  | 'radio'          // Radio buttons
  | 'checkbox'       // Single checkbox (boolean)
  | 'checkboxgroup'  // Multiple checkboxes
  | 'rating'         // Star rating (1-5)
  | 'file'           // File upload
  | 'color';         // Color picker

// ==================== Custom Field Definition ====================

export interface CustomFieldDefinition {
  id: number;
  field_name: string;           // Unique identifier (lowercase, underscore)
  field_label: string;          // Display label
  field_type: CustomFieldType;
  description?: string;
  is_required: boolean;
  is_searchable: boolean;       // Include in search queries
  is_visible: boolean;          // Show in UI
  field_options?: FieldOptions; // Type-specific options
  validation_rules?: ValidationRules;
  default_value?: string;
  display_order: number;
  field_group?: string;         // Group related fields
  created_by?: number;
  created_at: string;           // ISO string on frontend
  updated_at?: string;          // ISO string on frontend
  deleted_at?: string;          // ISO string on frontend
  deleted_by?: number;
}

export interface FieldOptions {
  // For select, multiselect, radio, checkboxgroup
  choices?: FieldChoice[];

  // For number, decimal, currency, percentage
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;  // e.g., "$" for currency
  suffix?: string;  // e.g., "%" for percentage

  // For text, textarea
  min_length?: number;
  max_length?: number;
  pattern?: string;  // Regex pattern

  // For textarea
  rows?: number;

  // For file
  allowed_extensions?: string[];
  max_file_size?: number;  // in bytes

  // For rating
  max_rating?: number;

  // For date, datetime
  min_date?: string;
  max_date?: string;

  // For all fields
  placeholder?: string;
  help_text?: string;
}

export interface FieldChoice {
  value: string;
  label: string;
  color?: string;
  icon?: string;
  is_default?: boolean;
}

export interface ValidationRules {
  required?: boolean;
  unique?: boolean;
  custom_regex?: string;
  custom_message?: string;
  depends_on?: {
    field_name: string;
    condition: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
    value: any;
  }[];
}

// ==================== Custom Field Value ====================

export interface CustomerCustomFieldValue {
  id: number;
  customer_id: number;
  field_definition_id: number;
  field_value?: string;           // For text, textarea, email, phone, url, select, radio, file
  field_value_number?: number;    // For number, decimal, currency, percentage, rating
  field_value_date?: string;      // For date, datetime (ISO string)
  field_value_boolean?: boolean;  // For checkbox
  field_value_json?: any;         // For multiselect, checkboxgroup, complex data
  created_at: string;
  updated_at?: string;

  // Populated fields
  field_definition?: CustomFieldDefinition;
}

// ==================== DTOs ====================

export interface CreateCustomFieldDefinitionDto {
  field_name: string;
  field_label: string;
  field_type: CustomFieldType;
  description?: string;
  is_required?: boolean;
  is_searchable?: boolean;
  is_visible?: boolean;
  field_options?: FieldOptions;
  validation_rules?: ValidationRules;
  default_value?: string;
  display_order?: number;
  field_group?: string;
  created_by?: number;
}

export interface UpdateCustomFieldDefinitionDto {
  field_label?: string;
  description?: string;
  is_required?: boolean;
  is_searchable?: boolean;
  is_visible?: boolean;
  field_options?: FieldOptions;
  validation_rules?: ValidationRules;
  default_value?: string;
  display_order?: number;
  field_group?: string;
}

export interface SetCustomFieldValueDto {
  customer_id: number;
  field_definition_id: number;
  value: any;  // Will be converted to appropriate field based on field_type
}

export interface BulkSetCustomFieldValuesDto {
  customer_id: number;
  values: {
    field_definition_id: number;
    value: any;
  }[];
}

// ==================== Query Parameters ====================

export interface CustomFieldDefinitionsQueryParams {
  field_type?: CustomFieldType;
  field_group?: string;
  is_visible?: boolean;
  is_searchable?: boolean;
  include_deleted?: boolean;
}

export interface CustomFieldValuesQueryParams {
  customer_id: number;
  field_definition_ids?: number[];
  include_definitions?: boolean;
}

// ==================== Response Types ====================

export interface CustomFieldDefinitionsResponse {
  success: boolean;
  data: CustomFieldDefinition[];
  total: number;
}

export interface CustomerWithCustomFields {
  customer_id: number;
  custom_fields: {
    [field_name: string]: {
      definition: CustomFieldDefinition;
      value: any;
      raw_value: CustomerCustomFieldValue;
    };
  };
}

export interface CustomFieldValueResponse {
  field_definition: CustomFieldDefinition;
  value: any;
  raw_value: CustomerCustomFieldValue;
}

// ==================== Helper Types ====================

export interface CustomFieldGroup {
  group_name: string;
  group_label: string;
  fields: CustomFieldDefinition[];
  display_order: number;
}

export interface CustomFieldSearchCriteria {
  field_definition_id: number;
  operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than' | 'between' | 'in' | 'is_null' | 'is_not_null';
  value: any;
  value2?: any;  // For 'between' operator
}

export interface CustomFieldsSearchQuery {
  criteria: CustomFieldSearchCriteria[];
  match_all?: boolean;  // AND vs OR
}

// ==================== UI Helper Types ====================

export interface CustomFieldFormData {
  [field_name: string]: any;
}

export interface CustomFieldValidationError {
  field_name: string;
  message: string;
}

export interface CustomFieldFormProps {
  customerId: number;
  fieldDefinitions: CustomFieldDefinition[];
  currentValues?: CustomerCustomFieldValue[];
  onSave: (values: CustomFieldFormData) => Promise<void>;
  onCancel?: () => void;
  readOnly?: boolean;
}

export interface CustomFieldInputProps {
  definition: CustomFieldDefinition;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  readOnly?: boolean;
}

// ==================== Utility Functions (Type Guards) ====================

export function isNumericFieldType(fieldType: CustomFieldType): boolean {
  return ['number', 'decimal', 'currency', 'percentage', 'rating'].includes(fieldType);
}

export function isDateFieldType(fieldType: CustomFieldType): boolean {
  return ['date', 'datetime'].includes(fieldType);
}

export function isSelectFieldType(fieldType: CustomFieldType): boolean {
  return ['select', 'multiselect', 'radio', 'checkboxgroup'].includes(fieldType);
}

export function isTextFieldType(fieldType: CustomFieldType): boolean {
  return ['text', 'textarea', 'email', 'phone', 'url'].includes(fieldType);
}

export function requiresChoices(fieldType: CustomFieldType): boolean {
  return isSelectFieldType(fieldType);
}

// ==================== Value Extraction Helpers ====================

/**
 * Extracts the appropriate value from a CustomerCustomFieldValue based on field type
 */
export function extractFieldValue(
  fieldValue: CustomerCustomFieldValue,
  fieldType: CustomFieldType
): any {
  if (isNumericFieldType(fieldType)) {
    return fieldValue.field_value_number;
  }

  if (isDateFieldType(fieldType)) {
    return fieldValue.field_value_date;
  }

  if (fieldType === 'checkbox') {
    return fieldValue.field_value_boolean;
  }

  if (fieldType === 'multiselect' || fieldType === 'checkboxgroup') {
    return fieldValue.field_value_json;
  }

  return fieldValue.field_value;
}

/**
 * Gets the display value for a custom field (formatted for UI)
 */
export function getFieldDisplayValue(
  value: any,
  definition: CustomFieldDefinition
): string {
  if (value === null || value === undefined) {
    return '-';
  }

  const { field_type, field_options } = definition;

  switch (field_type) {
    case 'currency':
      const prefix = field_options?.prefix || '$';
      return `${prefix}${Number(value).toFixed(2)}`;

    case 'percentage':
      const suffix = field_options?.suffix || '%';
      return `${value}${suffix}`;

    case 'rating':
      return `${'★'.repeat(value)}${'☆'.repeat((field_options?.max_rating || 5) - value)}`;

    case 'date':
      return new Date(value).toLocaleDateString();

    case 'datetime':
      return new Date(value).toLocaleString();

    case 'checkbox':
      return value ? 'Yes' : 'No';

    case 'multiselect':
    case 'checkboxgroup':
      if (Array.isArray(value)) {
        const choices = field_options?.choices || [];
        return value
          .map(v => choices.find(c => c.value === v)?.label || v)
          .join(', ');
      }
      return String(value);

    case 'select':
    case 'radio':
      const choice = field_options?.choices?.find(c => c.value === value);
      return choice?.label || value;

    default:
      return String(value);
  }
}

/**
 * Validates a field value based on definition rules
 */
export function validateFieldValue(
  value: any,
  definition: CustomFieldDefinition
): string | null {
  const { field_type, is_required, field_options, validation_rules } = definition;

  // Required check
  if (is_required && (value === null || value === undefined || value === '')) {
    return `${definition.field_label} is required`;
  }

  // Skip further validation if empty and not required
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // Type-specific validation
  switch (field_type) {
    case 'email':
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Invalid email format';
      }
      break;

    case 'url':
      try {
        new URL(value);
      } catch {
        return 'Invalid URL format';
      }
      break;

    case 'number':
    case 'decimal':
    case 'currency':
    case 'percentage':
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return 'Must be a valid number';
      }
      if (field_options?.min !== undefined && numValue < field_options.min) {
        return `Must be at least ${field_options.min}`;
      }
      if (field_options?.max !== undefined && numValue > field_options.max) {
        return `Must be at most ${field_options.max}`;
      }
      break;

    case 'text':
    case 'textarea':
      const strValue = String(value);
      if (field_options?.min_length && strValue.length < field_options.min_length) {
        return `Must be at least ${field_options.min_length} characters`;
      }
      if (field_options?.max_length && strValue.length > field_options.max_length) {
        return `Must be at most ${field_options.max_length} characters`;
      }
      if (field_options?.pattern) {
        const regex = new RegExp(field_options.pattern);
        if (!regex.test(strValue)) {
          return validation_rules?.custom_message || 'Invalid format';
        }
      }
      break;

    case 'date':
    case 'datetime':
      const dateValue = new Date(value);
      if (isNaN(dateValue.getTime())) {
        return 'Invalid date';
      }
      if (field_options?.min_date && dateValue < new Date(field_options.min_date)) {
        return `Date must be after ${new Date(field_options.min_date).toLocaleDateString()}`;
      }
      if (field_options?.max_date && dateValue > new Date(field_options.max_date)) {
        return `Date must be before ${new Date(field_options.max_date).toLocaleDateString()}`;
      }
      break;
  }

  // Custom regex validation
  if (validation_rules?.custom_regex) {
    const regex = new RegExp(validation_rules.custom_regex);
    if (!regex.test(String(value))) {
      return validation_rules.custom_message || 'Invalid format';
    }
  }

  return null;
}
